import React from "react";
import { useTranslation } from "react-i18next";
import { U2000_AGING_BUCKETS, type U2000AgingBucket } from "../../../u2000Core/constants/U2000AgingBuckets";
import { NS } from "../../U3134";
import {
  u3134FormatTecAgeCurrency,
  u3134TecAgeBucketMap,
  type U3134TecAgeBucketSummary,
  type U3134TecAgeTotals,
} from "../../U3134Service";

export interface U3134TecAgeBucketTableProps {
  buckets: U3134TecAgeBucketSummary[];
  totals: U3134TecAgeTotals;
  showTotalsColumn?: boolean;
  showHeader?: boolean;
  selectedBucket?: U2000AgingBucket | null;
  linkableBuckets?: boolean;
  onSelectBucket?: (bucket: U2000AgingBucket | null) => void;
  variant?: "default" | "compact";
}

export function u3134TecAgeBucketColumnClass(
  code: U2000AgingBucket,
  selectedBucket: U2000AgingBucket | null | undefined,
): string {
  return selectedBucket === code ? "bg-primary-50/80" : "";
}

export function u3134FormatTecAgeAmountWithLineCount(amount: number, lineCount: number): string {
  return `${u3134FormatTecAgeCurrency(amount)} (${lineCount})`;
}

export interface U3134TecAgeAmountWithLineCountProps {
  amount: number;
  lineCount: number;
  countClassName?: string;
}

const u3134TecAgeCountColumnClass = "inline-block min-w-7 shrink-0 pl-1 text-right";

export interface U3134TecAgeBucketColumnCellLayoutProps {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  primaryClassName?: string;
  secondaryClassName?: string;
  reserveCountColumn?: boolean;
  tabular?: boolean;
}

/** Grille partagée en-tête période / montant / (nb lignes) — colonnes alignées. */
export const U3134TecAgeBucketColumnCellLayout: React.FC<U3134TecAgeBucketColumnCellLayoutProps> = ({
  primary,
  secondary,
  primaryClassName = "",
  secondaryClassName = "text-muted-foreground",
  reserveCountColumn = true,
  tabular = true,
}) => (
  <span className={`flex w-full items-baseline justify-end gap-1 ${tabular ? "tabular-nums" : ""}`}>
    <span className={`text-right whitespace-nowrap ${primaryClassName}`}>{primary}</span>
    {reserveCountColumn ? (
      secondary != null ? (
        <span className={`${u3134TecAgeCountColumnClass} ${secondaryClassName}`}>{secondary}</span>
      ) : (
        <span className={`${u3134TecAgeCountColumnClass} invisible select-none`} aria-hidden>
          &#8203;
        </span>
      )
    ) : null}
  </span>
);

export const U3134TecAgeAmountWithLineCount: React.FC<U3134TecAgeAmountWithLineCountProps> = ({
  amount,
  lineCount,
  countClassName = "text-muted-foreground",
}) => (
  <U3134TecAgeBucketColumnCellLayout
    primary={u3134FormatTecAgeCurrency(amount)}
    secondary={`(${lineCount})`}
    secondaryClassName={countClassName}
  />
);

const u3134TecAgeBucketHeaderCellClass = "pb-1 px-1 text-right font-medium whitespace-nowrap";
const u3134TecAgeBucketBodyCellClass = "px-1 text-right";

export interface U3134TecAgeBucketTableHeaderProps {
  showTotalsColumn?: boolean;
  selectedBucket?: U2000AgingBucket | null;
  linkableBuckets?: boolean;
  onSelectBucket?: (bucket: U2000AgingBucket | null) => void;
  /** Aligne libellés période sur la grille montant / (nb lignes) — à utiliser avec layout singleRow. */
  alignPeriodWithAmountLineCount?: boolean;
}

function u3134TecAgeBucketHeaderCellContent(
  label: React.ReactNode,
  alignPeriodWithAmountLineCount: boolean,
  primaryClassName = "",
): React.ReactNode {
  if (!alignPeriodWithAmountLineCount) return label;
  return (
    <U3134TecAgeBucketColumnCellLayout
      primary={label}
      primaryClassName={primaryClassName}
      tabular={false}
    />
  );
}

export const U3134TecAgeBucketTableHeader: React.FC<U3134TecAgeBucketTableHeaderProps> = ({
  showTotalsColumn = true,
  selectedBucket = null,
  linkableBuckets = false,
  onSelectBucket,
  alignPeriodWithAmountLineCount = false,
}) => {
  const { t } = useTranslation();

  const handleBucketClick = (code: U2000AgingBucket) => {
    if (!linkableBuckets || !onSelectBucket) return;
    onSelectBucket(selectedBucket === code ? null : code);
  };

  const bucketHeaderButtonClass = alignPeriodWithAmountLineCount
    ? "block w-full text-right text-primary-600 hover:underline font-medium"
    : "text-primary-600 hover:underline font-medium";

  return (
    <thead>
      <tr className="text-left text-muted-foreground">
        <th className="pb-1 pr-2 font-medium whitespace-nowrap" />
        {U2000_AGING_BUCKETS.map((code) => {
          const label = t(`tecAge.bucket.${code}`, { ns: NS, defaultValue: code });
          const colClass = `${u3134TecAgeBucketHeaderCellClass} ${u3134TecAgeBucketColumnClass(code, selectedBucket)}`;
          const cellContent = u3134TecAgeBucketHeaderCellContent(label, alignPeriodWithAmountLineCount);
          if (linkableBuckets && onSelectBucket) {
            return (
              <th key={code} className={colClass}>
                <button
                  type="button"
                  className={bucketHeaderButtonClass}
                  onClick={() => handleBucketClick(code)}
                  title={t("lblTecAgeFilterByBucket", { ns: NS, bucket: label })}
                >
                  {cellContent}
                </button>
              </th>
            );
          }
          return (
            <th key={code} className={colClass}>
              {cellContent}
            </th>
          );
        })}
        {showTotalsColumn ? (
          <th className="pb-1 pl-2 text-right font-medium whitespace-nowrap">
            {linkableBuckets && onSelectBucket && selectedBucket ? (
              <button
                type="button"
                className={bucketHeaderButtonClass}
                onClick={() => onSelectBucket(null)}
                title={t("btnTecAgeClearBucketFilter", { ns: NS })}
              >
                {u3134TecAgeBucketHeaderCellContent(
                  t("lblTecAgeTotal", { ns: NS }),
                  alignPeriodWithAmountLineCount,
                )}
              </button>
            ) : (
              u3134TecAgeBucketHeaderCellContent(
                t("lblTecAgeTotal", { ns: NS }),
                alignPeriodWithAmountLineCount,
              )
            )}
          </th>
        ) : null}
      </tr>
    </thead>
  );
};

export interface U3134TecAgeBucketMetricRowsProps {
  buckets: U3134TecAgeBucketSummary[];
  totals: U3134TecAgeTotals;
  showTotalsColumn?: boolean;
  selectedBucket?: U2000AgingBucket | null;
  variant?: "default" | "compact";
  layout?: "dualRow" | "singleRow";
  countLabel?: string;
  amountLabel?: string;
  countLabelClassName?: string;
  amountLabelClassName?: string;
  firstProjectRow?: boolean;
}

export const U3134TecAgeBucketMetricRows: React.FC<U3134TecAgeBucketMetricRowsProps> = ({
  buckets,
  totals,
  showTotalsColumn = true,
  selectedBucket = null,
  variant = "default",
  layout = "dualRow",
  countLabel,
  amountLabel,
  countLabelClassName = "",
  amountLabelClassName = "",
  firstProjectRow = false,
}) => {
  const { t } = useTranslation();
  const bucketMap = u3134TecAgeBucketMap(buckets);
  const rowPy = variant === "compact" ? "py-0.5" : "py-1";
  const resolvedCountLabel = countLabel ?? t("colTecAgeLineCount", { ns: NS });
  const resolvedAmountLabel = amountLabel ?? t("colTecAgeTotalAmount", { ns: NS });
  const projectBorder = firstProjectRow ? "border-t-2 border-gray-200" : "border-t border-gray-100";

  if (layout === "singleRow") {
    return (
      <tr className={firstProjectRow ? projectBorder : "border-t border-gray-100"}>
        <td className={`${rowPy} pr-2 font-medium whitespace-nowrap ${countLabelClassName}`}>
          {resolvedCountLabel}
        </td>
        {U2000_AGING_BUCKETS.map((code) => {
          const bucket = bucketMap.get(code);
          return (
            <td
              key={`single-${code}`}
              className={`${rowPy} ${u3134TecAgeBucketBodyCellClass} ${u3134TecAgeBucketColumnClass(code, selectedBucket)}`}
            >
              <U3134TecAgeAmountWithLineCount
                amount={bucket?.totalAmount ?? 0}
                lineCount={bucket?.lineCount ?? 0}
              />
            </td>
          );
        })}
        {showTotalsColumn ? (
          <td className={`${rowPy} pl-2 text-right font-medium`}>
            <U3134TecAgeAmountWithLineCount
              amount={totals.totalAmount ?? 0}
              lineCount={totals.lineCount ?? 0}
            />
          </td>
        ) : null}
      </tr>
    );
  }

  return (
    <>
      <tr className={firstProjectRow ? projectBorder : "border-t border-gray-100"}>
        <td className={`${rowPy} pr-2 font-medium whitespace-nowrap ${countLabelClassName}`}>
          {resolvedCountLabel}
        </td>
        {U2000_AGING_BUCKETS.map((code) => (
          <td
            key={`count-${code}`}
            className={`${rowPy} px-1 text-right tabular-nums ${u3134TecAgeBucketColumnClass(code, selectedBucket)}`}
          >
            {bucketMap.get(code)?.lineCount ?? 0}
          </td>
        ))}
        {showTotalsColumn ? (
          <td className={`${rowPy} pl-2 text-right tabular-nums font-medium`}>{totals.lineCount ?? 0}</td>
        ) : null}
      </tr>
      <tr className="border-t border-gray-100">
        <td className={`${rowPy} pr-2 font-medium text-muted-foreground whitespace-nowrap ${amountLabelClassName}`}>
          {resolvedAmountLabel}
        </td>
        {U2000_AGING_BUCKETS.map((code) => (
          <td
            key={`amt-${code}`}
            className={`${rowPy} px-1 text-right tabular-nums ${u3134TecAgeBucketColumnClass(code, selectedBucket)}`}
          >
            {u3134FormatTecAgeCurrency(bucketMap.get(code)?.totalAmount ?? 0)}
          </td>
        ))}
        {showTotalsColumn ? (
          <td className={`${rowPy} pl-2 text-right tabular-nums font-medium`}>
            {u3134FormatTecAgeCurrency(totals.totalAmount ?? 0)}
          </td>
        ) : null}
      </tr>
    </>
  );
};

const U3134TecAgeBucketTable: React.FC<U3134TecAgeBucketTableProps> = ({
  buckets,
  totals,
  showTotalsColumn = true,
  showHeader = true,
  selectedBucket = null,
  linkableBuckets = false,
  onSelectBucket,
  variant = "default",
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-160 text-xs">
        {showHeader ? (
          <U3134TecAgeBucketTableHeader
            showTotalsColumn={showTotalsColumn}
            selectedBucket={selectedBucket}
            linkableBuckets={linkableBuckets}
            onSelectBucket={onSelectBucket}
          />
        ) : null}
        <tbody>
          <U3134TecAgeBucketMetricRows
            buckets={buckets}
            totals={totals}
            showTotalsColumn={showTotalsColumn}
            selectedBucket={selectedBucket}
            variant={variant}
          />
        </tbody>
      </table>
    </div>
  );
};

export default U3134TecAgeBucketTable;
